import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { fetchHelpers } from "@/lib/api";

const SKILL_OPTIONS = ["All", "Cooking", "Housekeeping", "Infant Care", "Elderly Care", "Special Needs Care", "Childcare", "Tutoring", "Pet Care", "Laundry"];
const EXPERIENCE_OPTIONS = [
  { label: "Any", value: 0 },
  { label: "3+ years", value: 3 },
  { label: "5+ years", value: 5 },
  { label: "10+ years", value: 10 },
];

export default function SearchMaid() {
  const [helpers, setHelpers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skill, setSkill] = useState("All");
  const [minExp, setMinExp] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    fetchHelpers().then((d) => { setHelpers(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return helpers.filter((h) => {
      if (skill !== "All" && !h.skills.map(s => s.toLowerCase()).includes(skill.toLowerCase())) return false;
      if (h.experience_years < minExp) return false;
      if (search) {
        const hay = (h.name + " " + h.bio + " " + h.skills.join(" ")).toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [helpers, skill, minExp, search]);

  return (
    <div data-testid="search-maid-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
        <div className="overline mb-4">Search Maid</div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] text-[#0A1128] mb-4">Meet helpers, <span className="italic text-[#688A4E]">carefully vetted</span>.</h1>
        <p className="text-lg text-gray-600 max-w-2xl">Browse profiles scored by our Happy Home Matching Score™. Every helper is pre-screened for skills, values and long-term fit.</p>

        <a
          href="https://happyhome.netmaid.com.sg/searchmaid"
          target="_blank"
          rel="noreferrer"
          data-testid="external-db-cta"
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-[#0A1128] text-white px-6 sm:px-10 py-8 hover:bg-[#131b3a] transition group"
        >
          <div>
            <div className="overline text-[#688A4E] mb-2">Full Live Database</div>
            <div className="font-serif text-2xl sm:text-3xl leading-tight">Browse our complete helper database →</div>
            <div className="text-sm text-[#A1A9BC] mt-2">Real-time listings on happyhome.netmaid.com.sg with filters, photos and full biodata.</div>
          </div>
          <div className="border border-white/30 px-6 py-3 text-sm tracking-wider uppercase group-hover:bg-white group-hover:text-[#0A1128] transition whitespace-nowrap">
            Open Full Database ↗
          </div>
        </a>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Filters */}
          <aside className="lg:col-span-3">
            <div className="border border-gray-200 p-6 sticky top-24 bg-white">
              <div className="overline mb-4">Filter</div>

              <div className="mb-6">
                <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Search</label>
                <div className="relative">
                  <input
                    data-testid="filter-search-input"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name or keyword"
                    className="w-full border-b border-gray-300 py-2 pl-6 bg-transparent focus:outline-none focus:border-[#688A4E] text-sm"
                  />
                  <Search className="w-4 h-4 text-gray-400 absolute left-0 top-2.5"/>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-xs tracking-widest uppercase text-gray-500 mb-3">Skill</label>
                <div className="flex flex-col gap-2">
                  {SKILL_OPTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`filter-skill-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setSkill(s)}
                      className={`text-left text-sm py-1 hh-underline pb-1 w-fit ${skill === s ? "text-[#688A4E] font-semibold" : "text-gray-700"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-2">
                <label className="block text-xs tracking-widest uppercase text-gray-500 mb-3">Experience</label>
                <div className="flex flex-col gap-2">
                  {EXPERIENCE_OPTIONS.map((e) => (
                    <button
                      key={e.value}
                      data-testid={`filter-exp-${e.value}`}
                      onClick={() => setMinExp(e.value)}
                      className={`text-left text-sm py-1 hh-underline pb-1 w-fit ${minExp === e.value ? "text-[#688A4E] font-semibold" : "text-gray-700"}`}
                    >
                      {e.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Results */}
          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-gray-600" data-testid="results-count">{loading ? "Loading…" : `${filtered.length} helpers found`}</div>
              <div className="text-xs tracking-widest uppercase text-gray-400">Sorted by Matching Score™</div>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="border border-gray-200 p-6 h-80 animate-pulse bg-[#F9F9F7]"/>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="border border-gray-200 p-16 text-center">
                <div className="font-serif text-2xl text-[#0A1128] mb-2">No helpers match your filters.</div>
                <p className="text-gray-600">Try broadening your criteria or contact us for a personal shortlist.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6">
                {filtered.sort((a, b) => b.matching_score - a.matching_score).map((h) => (
                  <Link
                    key={h.id}
                    to={`/biodata/${h.id}`}
                    data-testid={`helper-card-${h.id}`}
                    className="group border border-gray-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-all block"
                  >
                    <div className="relative overflow-hidden h-72">
                      <img src={h.photo} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
                      <div className="absolute top-4 right-4 bg-[#688A4E] text-white px-3 py-2 text-xs tracking-wider">
                        <div className="text-[10px] opacity-80">SCORE</div>
                        <div className="font-serif text-xl leading-none">{h.matching_score}</div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-baseline justify-between mb-2">
                        <h3 className="font-serif text-2xl text-[#0A1128] group-hover:text-[#688A4E] transition">{h.name}</h3>
                        <span className="text-xs text-gray-500">{h.age} yrs</span>
                      </div>
                      <div className="text-sm text-gray-600 mb-4">{h.nationality} · {h.experience_years} yrs experience</div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {h.skills.slice(0, 3).map((s) => (
                          <span key={s} className="text-xs border border-gray-300 px-2 py-1 text-gray-700">{s}</span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#688A4E]">View biodata <ArrowRight className="w-4 h-4"/></div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
